import { DBMessageLogThunks } from "@common-reducers/DBServiceThunks";
import { AppThunkDispatch, CommonRootState } from "@common/types/common-root-state-type";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { ackDataFromAck, AckData } from "@bt-common/services/message-status";
import { recordMessageDeliveryAck } from "@extension/wa-communication-layer/api/api-calls";

/**
 * Pure builder: correlates the polled `{key, ack}[]` (waMessageKey → real
 * wa-js ack) back to `messageId` via the same batch we sent, and shapes the
 * REAL ack + its derived `*Remaining` detail into `recordMessageDeliveryAck`
 * call params. Never a hardcoded `ackData: {}` — that was the flood bug
 * (an empty ackData routed through the append path and wrote bogus
 * `status:'played'` rows). Entries with no matching messageId are dropped
 * rather than written with a blank id.
 */
export function buildDeliveryAckReconcileCalls(
    acks: Array<{ key: string; ack: number }>,
    logsWithKeys: Array<{ messageId: string; waMessageKey: string }>,
): Array<{ messageId: string; ack: number; ackData: AckData; waMessageKey: string }> {
    return acks
        .map((entry) => {
            const messageId = logsWithKeys.find((log) => log.waMessageKey === entry.key)?.messageId;
            if (!messageId) return null;
            return { messageId, ack: entry.ack, ackData: ackDataFromAck(entry.ack, 1), waMessageKey: entry.key };
        })
        .filter((call): call is { messageId: string; ack: number; ackData: AckData; waMessageKey: string } => call !== null);
}

export const waGetMessagesAckAndCalculateStatisticsTool = createAsyncThunk<
    { campaignMessageLogs: any[]; messagesKeys: any[]; messageLogWithoutWaKeys: any[]; realMessagesAckData: any[]; recordedCount: number },
    string,
    { state: CommonRootState }
>(
    "campaigns/waGetMessagesAckAndCalculateStatisticsTool",
    async (campaignId, thunkAPI) => {
        const dispatch = thunkAPI.dispatch as AppThunkDispatch;

        const campaignMessageLogs = await dispatch(
            DBMessageLogThunks.find({
                campaignId,
                $sort: { createdAt: -1 },
                $limit: -1,
                $getMostRecentPerMessageId: true,
            })
        ).unwrap();

        const messageLogWithWaKeys = campaignMessageLogs
            .filter((log: { waMessageKey?: string }) => log.waMessageKey)
            .map((log: { messageId: string; waMessageKey: string }) => ({
                messageId: log.messageId,
                waMessageKey: log.waMessageKey,
            }));

        const messageLogWithoutWaKeys = campaignMessageLogs
            .filter((log: { waMessageKey?: string }) => !log.waMessageKey)
            .map((log: { messageId: string }) => ({
                messageId: log.messageId
            }));


        // const missingMessageLoghWaKeys =  getMissingMessageLoghWaKeysByMessagesIds(messageLogWithoutWaKeys);
        const missingMessageLoghWaKeys: Array<{ messageId: string; waMessageKey: string }> = []

        const messageLogWithWaKeysCombined : any[] =  [...messageLogWithWaKeys, ...missingMessageLoghWaKeys];


        if (!messageLogWithWaKeysCombined || messageLogWithWaKeysCombined.length === 0) {
            console.log('No messages keys found for campaign:', campaignId);
            return { campaignMessageLogs, messagesKeys: messageLogWithWaKeys, messageLogWithoutWaKeys, realMessagesAckData: [], recordedCount: 0 };
        }

        // Get message acknowledgment data via gold-standard tool (handles extension/server mode internally)
        const { waGetMessagesAckDataTool } = await import(/* webpackMode: "eager" */ "@extension/context/content/thunks/WhatsappThunks");
        const ackResponse = await waGetMessagesAckDataTool({ messageKeys: messageLogWithWaKeysCombined });

        if (ackResponse.success === false) {
            console.error('Failed to get messages ack data:', ackResponse.error.message);
            return { campaignMessageLogs, messagesKeys: messageLogWithWaKeys, messageLogWithoutWaKeys, realMessagesAckData: [], recordedCount: 0 };
        }

        // Canonical wire shape (#120 / BE#93): the contract's
        // WaGetMessagesAckDataResultSchema is the flat array
        // BtSlimBatchMessageAckEntry[] = `{key, ack}[]`. The legacy
        // `{ackData}` wrapper is gone, as are the per-recipient
        // `*Remaining` fields (not on the public v1 contract — see
        // getMessageAckDataByKey in message-retrieval-operations.ts).
        // We correlate `key` (waMessageKey) back to messageId via the
        // input batch we sent so polling can still compute per-message
        // log updates.
        const realMessagesAckData = (ackResponse.data ?? []) as Array<{ key: string; ack: number }>;
        console.log('realMessagesAckData', realMessagesAckData);

        // Current stored ack + status per message (from the logs we just fetched) so we
        // ONLY write acks that actually ADVANCED. Without this, a steady-state poll re-POSTed
        // record-message-delivery-ack for every message every cycle (monotonic no-ops) AND
        // re-fetched the whole log set — ~8 requests every 10s even when nothing changed.
        const currentByMessageId = new Map<string, { ack: number; status: string }>();
        for (const log of campaignMessageLogs as Array<{ messageId?: string; ack?: number; status?: string }>) {
            if (log.messageId) currentByMessageId.set(log.messageId, { ack: log.ack ?? 0, status: log.status ?? '' });
        }

        // Reconcile the REAL ack in place via the durable, monotonic writer (never the
        // append/create path). Skip a message whose ack did NOT advance and isn't a pending
        // log needing promotion, and skip null acks (they'd just fail validation).
        const deliveryAckCalls = buildDeliveryAckReconcileCalls(realMessagesAckData, messageLogWithWaKeysCombined)
            .filter((call) => {
                if (typeof call.ack !== 'number') return false;
                const cur = currentByMessageId.get(call.messageId);
                if (!cur) return true;
                return call.ack > cur.ack || (call.ack >= 1 && cur.status === 'sent_pending_ack');
            });

        let recordedCount = 0;
        if (deliveryAckCalls.length > 0) {
            const results = await Promise.all(
                deliveryAckCalls.map((call) =>
                    recordMessageDeliveryAck(call.messageId, call.ack, call.ackData, call.waMessageKey)
                )
            );

            const failed = results.filter((r) => !r.success);
            recordedCount = results.filter((r) => r.success).length;
            if (failed.length > 0) {
                console.error(`[Polling] Failed to record ${failed.length} delivery ack(s) for campaign ${campaignId}:`, failed.map((r) => r.error));
            } else {
                console.log(`[Polling] Recorded ${recordedCount} delivery ack(s) for campaign ${campaignId}`);
            }
        }

        // Re-load the logs into the store ONLY when an ack actually advanced, so the campaign
        // detail page reflects the change without opening "View delivery logs" — but a settled
        // poll (nothing advanced) does zero record + zero re-fetch. The write bypasses the
        // feathers/MQTT patch, so the re-fetch is how THIS tab catches up (other tabs get the
        // MQTT broadcast recordDeliveryAck now emits).
        if (recordedCount > 0) {
            await dispatch(DBMessageLogThunks.find({
                campaignId,
                $sort: { createdAt: -1 },
                $limit: -1,
                $getMostRecentPerMessageId: true,
            }));
        }

        return { campaignMessageLogs, messagesKeys: messageLogWithWaKeys, messageLogWithoutWaKeys, realMessagesAckData, recordedCount };
    }
);
